/**
 * Client-side page event tracking.
 *
 * Calls must originate from the browser (not server) so the API
 * receives the real client IP for geolocation.
 *
 * Delivery prefers navigator.sendBeacon: the browser queues the request
 * and delivers it even if the page unloads before it completes (card
 * gateway redirects, merchant redirect_url, tab close). A plain fetch —
 * even with keepalive — fired right before window.location.href was
 * frequently dropped, which undercounted clicks and completions.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, "");

type TrackingEvent =
  | { event: "view"; referrer: string }
  | { event: "click_pay"; session_ref: string }
  | { event: "payment_initiated"; session_ref: string }
  | { event: "payment_completed"; session_ref: string }
  | { event: "payment_failed"; session_ref: string };

declare global {
  interface Window {
    /** Slug whose "view" event was already reported this page load —
     * set by the inline early-tracking script or by trackPageView. */
    __snippeViewedSlug?: string;
  }
}

export function trackPageEvent(pageId: string, payload: TrackingEvent) {
  if (!API_URL || typeof window === "undefined") return;

  const url = `${API_URL}/t/${pageId}`;
  const body = JSON.stringify(payload);

  if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    } catch {
      // fall through to fetch
    }
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    // Anonymous beacon — never send cookies. Keeps the request uncredentialed
    // so the tracking endpoint can respond with a wildcard CORS origin.
    credentials: "omit",
  }).catch(() => {
    // Silently ignore tracking failures
  });
}

/**
 * Report the initial page view exactly once per page load. The inline
 * script from earlyViewTrackingScript normally fires first (pre-
 * hydration); this is the fallback for browsers where it didn't run.
 */
export function trackPageView(slug: string) {
  if (typeof window === "undefined") return;
  if (window.__snippeViewedSlug === slug) return;
  window.__snippeViewedSlug = slug;
  trackPageEvent(slug, { event: "view", referrer: document.referrer });
}

/**
 * Inline script that reports the "view" event as soon as the HTML is
 * parsed — before React hydrates. Visitors on slow connections often
 * leave before hydration completes, and their views were silently
 * lost when tracking only ran from a useEffect.
 *
 * Rendered into the server component's HTML; must stay dependency-free.
 */
export function earlyViewTrackingScript(slug: string): string {
  if (!API_URL) return "";
  // Escape "<" so embedded values can never terminate the <script> tag
  const safeUrl = JSON.stringify(`${API_URL}/t/${slug}`).replace(/</g, "\\u003c");
  const safeSlug = JSON.stringify(slug).replace(/</g, "\\u003c");
  return (
    "(function(){try{" +
    `if(window.__snippeViewedSlug===${safeSlug})return;` +
    `window.__snippeViewedSlug=${safeSlug};` +
    'var b=JSON.stringify({event:"view",referrer:document.referrer});' +
    `if(navigator.sendBeacon){try{if(navigator.sendBeacon(${safeUrl},new Blob([b],{type:"application/json"})))return;}catch(e){}}` +
    `fetch(${safeUrl},{method:"POST",headers:{"Content-Type":"application/json"},body:b,keepalive:true,credentials:"omit"}).catch(function(){});` +
    "}catch(e){}})();"
  );
}
